import { Fetcher, objectToFormData } from '@this/src/util';
import React from 'react';
import { reportError } from '@this/lib/bugsnag';
import { createColors } from '../../shared/ui/theme/tokens/primitive-tokens/colors';
import { TextField } from '../../shared/form_elements/form_elements';
import { Loading } from '../../shared/ui/feedbacks/loading/loading';

const brandColors = createColors(1).brand;

import type UserJson from '../../../domain/user/user_json';

interface Props {}

interface UserResponse {
  user: UserJson;
}

interface State {
  email: string;
  invitationToken: string;
  organizationId: string;
  password: string;
  lastName: string;
  firstName: string;
  tel: string;
  submitting: boolean;
  succeed: boolean;
  isPrivacyChecked: boolean;
  errors: {
    email?: string;
    password?: string;
    last_name?: string;
    first_name?: string;
    tel?: string;
  };
  submitError: string | null;
  fetchError: string | null;
}

class ExpensesUsersInvitationEdit extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      email: '',
      invitationToken: utils.getParam('invitation_token'),
      organizationId: utils.getParam('organization_id'),
      password: '',
      lastName: '',
      firstName: '',
      tel: '',
      submitting: false,
      succeed: false,
      isPrivacyChecked: false,
      errors: {},
      submitError: null,
      fetchError: null
    };
  }

  componentDidMount() {
    this.fetchUser();
  }

  async fetchUser() {
    try {
      const params = { invitation_token: this.state.invitationToken, organization_id: this.state.organizationId };
      const res = await Fetcher.get<UserResponse>('/aiexpense/users/invitation/accept', params);
      this.setState({
        email: res.user.email,
        lastName: res.user.last_name || '',
        firstName: res.user.first_name || '',
        tel: res.user.tel || ''
      });
    } catch (e) {
      const message =
        e.response?.data?.errors?.base || '招待リンクが無効です。管理者に招待メールの再送を依頼してください。';
      this.setState({ fetchError: message });
    }
  }

  handlePasswordChange(password: string) {
    this.setState({ password });
  }

  handleLastNameChange(lastName: string) {
    this.setState({ lastName });
  }

  handleFirstNameChange(firstName: string) {
    this.setState({ firstName });
  }

  handleTelChange(tel: string) {
    this.setState({ tel });
  }

  changePrivacyCheck() {
    this.setState({ isPrivacyChecked: !this.state.isPrivacyChecked });
  }

  async handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    try {
      this.setState({ submitting: true, errors: {}, submitError: null });
      const params = {
        'user[last_name]': this.state.lastName,
        'user[first_name]': this.state.firstName,
        'user[tel]': this.state.tel,
        'user[password]': this.state.password,
        'user[invitation_token]': this.state.invitationToken,
        organization_id: this.state.organizationId
      };
      await Fetcher.put('/aiexpense/users/invitation.json', objectToFormData(params));
      this.setState({ submitting: false, succeed: true });
    } catch (e) {
      if (e.response.status === 400) {
        const responseErrors = e.response.data.errors;
        // errorsが配列で返された際にもエラーメッセージを表示できるように。
        if (Array.isArray(responseErrors)) {
          this.setState({ submitting: false, submitError: responseErrors.join('\n') });
        } else if (responseErrors.base) {
          this.setState({ submitting: false, submitError: responseErrors.base });
        } else {
          this.setState({ submitting: false, errors: responseErrors });
        }
      } else {
        this.setState({
          submitting: false,
          submitError: '通信エラーが発生しました。時間をおいて再度お試しください。'
        });
      }
    }
  }

  render() {
    try {
      const {
        email,
        password,
        lastName,
        firstName,
        tel,
        submitting,
        succeed,
        isPrivacyChecked,
        errors,
        submitError,
        fetchError
      } = this.state;
      const styles = getStyles();
      return (
        // NOTE: TextField の色・submit ボタン色は SCSS カスケード（.expenses-users-signup スコープ）で制御するためクラスを保持
        <div className="expenses-users-signup" style={styles.container}>
          <div style={styles.content}>
            {fetchError ? (
              <div className="error">{fetchError}</div>
            ) : succeed ? (
              <div className="gray">
                登録が完了しました。
                <br />
                <a href="/aiexpense" style={styles.termsLinkAnchor}>
                  トップページへ
                </a>
              </div>
            ) : (
              <form onSubmit={e => this.handleSubmit(e)}>
                <div style={styles.tagline}>招待を受け入れる</div>
                <div style={styles.emailField}>
                  <div style={styles.emailLabel}>メールアドレス</div>
                  <div style={styles.emailValue}>{email}</div>
                </div>
                <div style={styles.nameFieldRow}>
                  <div style={styles.nameFieldFirst}>
                    <TextField
                      label="姓（必須）"
                      value={lastName}
                      example="例：山田"
                      error={errors.last_name}
                      onChange={value => this.handleLastNameChange(value)}
                    />
                  </div>
                  <div style={styles.nameField}>
                    <TextField
                      label="名（必須）"
                      value={firstName}
                      example="例：一郎"
                      error={errors.first_name}
                      onChange={value => this.handleFirstNameChange(value)}
                    />
                  </div>
                </div>
                <TextField
                  label="パスワード（必須）"
                  value={password}
                  example=""
                  type="password"
                  error={errors.password}
                  onChange={value => this.handlePasswordChange(value)}
                />
                <TextField
                  label="電話番号（任意）"
                  value={tel}
                  example="例：03-1234-5678"
                  error={errors.tel}
                  onChange={value => this.handleTelChange(value)}
                />
                <p style={styles.termsLink}>
                  <input type="checkbox" onClick={() => this.changePrivacyCheck()} />
                  <a
                    href="https://aiexpense.cloud/kiyaku"
                    target="_blank"
                    rel="noreferrer noopener"
                    style={styles.termsLinkAnchor}
                  >
                    利用規約
                  </a>
                  ・
                  <a
                    href="https://transferdata.co.jp/privacy-policy"
                    target="_blank"
                    rel="noreferrer noopener"
                    style={styles.termsLinkAnchor}
                  >
                    個人情報の取扱いについて
                  </a>
                  に同意の上、ご登録ください
                </p>
                {submitting ? <Loading /> : <input type="submit" value="登録" disabled={!isPrivacyChecked} />}
                {submitError && <div className="error">{submitError}</div>}
              </form>
            )}
          </div>
        </div>
      );
    } catch (e) {
      reportError(e);
      return null;
    }
  }
}

const getStyles = () => ({
  container: {
    maxWidth: '600px',
    width: '100%',
    margin: '50px auto'
  },
  content: {
    height: 'fit-content' as const,
    padding: '20px',
    background: 'white',
    fontSize: '16px',
    fontWeight: 'bold' as const
  },
  tagline: {
    fontSize: '24px',
    fontWeight: 'bold' as const,
    marginBottom: '40px',
    textAlign: 'center' as const
  },
  emailField: {
    color: '#7d7d7d',
    fontWeight: 'normal' as const,
    marginBottom: '25px'
  },
  emailLabel: {
    fontSize: '12px',
    marginBottom: '4px'
  },
  emailValue: {
    fontSize: '16px'
  },
  nameFieldRow: {
    display: 'flex',
    alignItems: 'center' as const,
    marginBottom: '40px'
  },
  nameFieldFirst: {
    flexGrow: 999,
    marginRight: '20px'
  },
  nameField: {
    flexGrow: 999
  },
  termsLink: {
    color: '#7d7d7d',
    fontWeight: 'normal' as const,
    marginBottom: '30px'
  },
  termsLinkAnchor: {
    color: brandColors[500]
  }
});

export default ExpensesUsersInvitationEdit;
